import type { AppBoleta } from '../../types/planManejo';

function esc(value?: string | number | null) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function fecha(value?: string | null) {
  if (!value) return '—';
  const d = new Date(String(value).includes('T') ? value : `${value}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

const ESTADOS: Record<string, string> = {
  pendiente: 'Pendiente',
  en_proceso: 'En proceso',
  impresa: 'Impresa',
  aplicada: 'Aplicada',
  archivada: 'Archivada',
  cancelada: 'Cancelada',
};

export interface FiltrosControlCedulas {
  estado: string;
  tipo?: 'oficina' | 'activo-biologico';
  semana?: string;
  semanaEnProceso?: string;
  corteContable?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  filtroFechasActivo: boolean;
  boleta?: string;
}

export type BoletaControlCedulas = AppBoleta & {
  control_ab?: string;
  movimientos_ab?: number;
};

export function buildControlCedulasHtml(
  boletas: BoletaControlCedulas[],
  filtros: FiltrosControlCedulas,
  empresaNombre: string,
  empresaCedula: string,
  empresaLogo = '',
): string {
  const esSeguimientoAb = filtros.tipo === 'activo-biologico';
  const estado = filtros.estado === 'todos'
    ? 'Todos los estados'
    : filtros.estado === 'manual'
      ? 'Cédulas manuales'
      : filtros.estado === 'vfp'
        ? 'Histórico VFP'
        : ESTADOS[filtros.estado] ?? filtros.estado;
  const periodo = filtros.semana
    ? `Semana ${filtros.semana}`
    : filtros.filtroFechasActivo && filtros.fechaDesde && filtros.fechaHasta
      ? `${fecha(filtros.fechaDesde)} al ${fecha(filtros.fechaHasta)}`
      : 'Todo el historial visible';
  const filtroBoleta = filtros.boleta ? ` · Boleta contiene: ${filtros.boleta}` : '';
  const exclusion = esSeguimientoAb
    ? filtros.corteContable
      ? ` · Aplicadas después del corte firme ${fecha(filtros.corteContable)}`
      : ' · Aplicadas después del último corte firme'
    : filtros.semanaEnProceso
      ? ` · Excluye semana en proceso ${filtros.semanaEnProceso} y cédulas resueltas`
      : ' · Excluye cédulas resueltas';

  const filas = boletas.map((b, i) => `
    <tr>
      <td class="n">${i + 1}</td>
      <td class="check">□</td>
      <td class="boleta">${esc(b.numero)}</td>
      <td>${esc(b.semana)}</td>
      <td>${esc(ESTADOS[b.estado] ?? b.estado)}</td>
      <td>${fecha(b.fecha_aplicacion)}</td>
      <td class="lote">${esc(b.lote_num)}</td>
      <td>${esc(b.etapa_nombre)}</td>
      <td>${esc(b.ciclo_nombre)}</td>
      ${esSeguimientoAb ? `
      <td class="control-ab">${esc(b.control_ab || 'Pendiente de revisar')}</td>
      <td class="movimientos">${esc(b.movimientos_ab ?? 0)}</td>` : ''}
      <td class="manual"></td>
      <td class="manual"></td>
      <td class="obs"></td>
    </tr>`).join('');

  const totalColumnas = esSeguimientoAb ? 14 : 12;
  const html = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>${esSeguimientoAb ? 'Seguimiento AB de cédulas' : 'Control manual de cédulas'}</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; color: #111; background: #fff; font-family: Arial, Helvetica, sans-serif; font-size: 8.5pt; }
  @page { size: Letter landscape; margin: 9mm 10mm 11mm; }
  /* En pantalla (vista previa) se ve muy pegado al borde sin esto — en
     impresión real el margen ya lo da @page, así que este padding solo
     aplica cuando se está viendo, no cuando se imprime. */
  @media screen {
    body { padding: 22px 28px; }
  }
  .encabezado { display: flex; align-items: center; gap: 12px; padding-bottom: 6px; border-bottom: 2px solid #173f75; }
  .logo { width: 58px; height: 52px; object-fit: contain; }
  .empresa { flex: 1; text-align: center; }
  .empresa h1 { margin: 0; color: #123f82; font-size: 15pt; }
  .empresa p { margin: 2px 0 0; font-size: 8pt; }
  .titulo { width: 270px; text-align: right; }
  .titulo h2 { margin: 0; font-size: 13pt; }
  .titulo p { margin: 3px 0 0; color: #555; font-size: 8pt; }
  .filtros { display: flex; justify-content: space-between; margin: 7px 0; padding: 5px 7px; background: #edf3fb; border: 1px solid #9eafc5; }
  .filtros strong { color: #173f75; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  thead { display: table-header-group; }
  th { padding: 5px 3px; color: #fff; background: #173f75; border: 1px solid #0c2b55; font-size: 7.3pt; text-transform: uppercase; }
  td { height: 25px; padding: 3px; border: 1px solid #9da8b5; vertical-align: middle; }
  tbody tr:nth-child(even) td { background: #f4f7fb; }
  tr { break-inside: avoid; page-break-inside: avoid; }
  .n { width: 24px; text-align: center; color: #555; }
  .check { width: 34px; text-align: center; font-size: 15pt; }
  .boleta { width: 82px; font-family: Consolas, monospace; font-weight: bold; }
  .lote { width: 38px; text-align: center; font-weight: bold; }
  .manual { width: 76px; }
  .control-ab { width: 105px; font-weight: bold; }
  .movimientos { width: 42px; text-align: center; }
  .obs { width: 130px; }
  .pie { display: flex; justify-content: space-between; margin-top: 6px; padding-top: 4px; border-top: 1px solid #aaa; color: #555; font-size: 7pt; }
  @media print {
    body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .encabezado, .filtros, .pie { break-inside: avoid; page-break-inside: avoid; }
  }
</style>
</head>
<body>
  <div class="encabezado">
    <div style="width:58px">${empresaLogo ? `<img class="logo" src="${esc(empresaLogo)}" alt="Logo">` : ''}</div>
    <div class="empresa">
      <h1>${esc(empresaNombre)}</h1>
      <p>Cédula Jurídica ${esc(empresaCedula)}</p>
    </div>
    <div class="titulo">
      <h2>${esSeguimientoAb ? 'Seguimiento de cédulas — Activo Biológico' : 'Control manual de cédulas'}</h2>
      <p>${esSeguimientoAb ? 'Control posterior al último corte contable firme' : 'Recepción y seguimiento en oficina'}</p>
    </div>
  </div>
  <div class="filtros">
    <span><strong>Filtro:</strong> ${esc(estado)} · ${esc(periodo)}${esc(filtroBoleta)}${esc(exclusion)}</span>
    <span><strong>Total:</strong> ${boletas.length} cédula${boletas.length === 1 ? '' : 's'}</span>
  </div>
  <table>
    <thead>
      <tr>
        <th class="n">#</th>
        <th class="check">Oficina</th>
        <th class="boleta">Boleta</th>
        <th>Semana</th>
        <th>Estado actual</th>
        <th>F. aplicada</th>
        <th class="lote">Lote</th>
        <th>Plan</th>
        <th>Ciclo</th>
        ${esSeguimientoAb ? '<th class="control-ab">Control AB</th><th class="movimientos">Mov.</th>' : ''}
        <th class="manual">Fecha recibido</th>
        <th class="manual">Recibido por</th>
        <th class="obs">Observaciones</th>
      </tr>
    </thead>
    <tbody>${filas || `<tr><td colspan="${totalColumnas}" style="text-align:center;padding:20px">No hay cédulas para los filtros seleccionados.</td></tr>`}</tbody>
  </table>
  <div class="pie">
    <span>${esSeguimientoAb ? 'Los costos cero y consumos sin movimiento permanecen fuera de la capitalización.' : 'Control manual basado en el estado visible al momento de imprimir.'}</span>
    <span>Impreso: ${new Date().toLocaleString('es-CR')}</span>
  </div>
</body>
</html>`;

  return html;
}

export function imprimirControlCedulas(
  boletas: BoletaControlCedulas[],
  filtros: FiltrosControlCedulas,
  empresaNombre: string,
  empresaCedula: string,
  empresaLogo = '',
) {
  const html = buildControlCedulasHtml(boletas, filtros, empresaNombre, empresaCedula, empresaLogo);

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:0;';
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    return;
  }
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  }, 500);
}
